import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { ReferencePoint } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { referencePointService } from "@/services/api/referencePoints";
import { Building2, Check, ChevronsUpDown, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { FormControl } from "./form";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Skeleton } from "./skeleton";

interface ReferencePointSelectProps {
  value?: number | number[];
  onChange: (value: number | number[]) => void;
  userId?: number;
  multiple?: boolean;
  placeholder?: string;
  className?: string;
  useFormControl?: boolean;
  disabled?: boolean;
  showType?: "headquarters" | "non-headquarters" | "all";
}

const ReferencePointSelect = ({
  value,
  onChange,
  userId,
  multiple = false,
  placeholder = "Selecione um ponto de referência",
  className,
  useFormControl = true,
  disabled = false,
  showType = "headquarters",
}: ReferencePointSelectProps) => {
  const [referencePoints, setReferencePoints] = useState<ReferencePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadReferencePoints = async () => {
      try {
        setLoading(true);
        const response = await referencePointService.index({
          page: 1,
        });
        setReferencePoints(response.data);
      } catch (err) {
        errorHandler(err, "Erro ao carregar pontos de referência.");
      } finally {
        setLoading(false);
      }
    };

    loadReferencePoints();
  }, [userId]);

  const handleSelect = (pointId: number) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(pointId)
        ? currentValues.filter((id) => id !== pointId)
        : [...currentValues, pointId];
      onChange(newValues);
    } else {
      onChange(pointId);
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allPointIds = filteredReferencePoints.map((point) => point.id);
      onChange(allPointIds);
    }
  };

  const handleDeselectAll = () => {
    if (multiple) {
      onChange([]);
    }
  };

  const getSelectedLabel = () => {
    if (multiple) {
      const selectedIds = Array.isArray(value) ? value : [];
      const selectedPoints = referencePoints.filter((point) =>
        selectedIds.includes(point.id),
      );
      return selectedPoints.length > 0
        ? selectedPoints.length === 1
          ? selectedPoints[0].name
          : `${selectedPoints.length} pontos selecionados`
        : getPlaceholder();
    } else {
      if (!value || (Array.isArray(value) && value.length === 0))
        return getPlaceholder();
      const selectedPoint = referencePoints.find((point) => point.id === value);
      return selectedPoint ? selectedPoint.name : getPlaceholder();
    }
  };

  const filteredReferencePoints = referencePoints.filter(
    (point) =>
      (showType === "headquarters" && point.is_headquarters) ||
      (showType === "non-headquarters" && !point.is_headquarters) ||
      showType === "all",
  );

  // Dynamic placeholder based on showType
  const getPlaceholder = () => {
    switch (showType) {
      case "headquarters":
        return "Selecione um ponto";
      case "non-headquarters":
        return "Selecione um ponto de referência";
      case "all":
        return "Selecione um ponto";
      default:
        return placeholder;
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full bg-muted" />
      </div>
    );
  }

  const ButtonComponent = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={loading || disabled}
      className={cn(
        "justify-between w-full",
        (!value || (multiple && Array.isArray(value) && value.length === 0)) &&
          "text-muted-foreground",
        "dark:border-white",
        className,
      )}
    >
      {getSelectedLabel()}
      <ChevronsUpDown className="opacity-50" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {useFormControl ? (
          <FormControl>{ButtonComponent}</FormControl>
        ) : (
          ButtonComponent
        )}
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder={
              showType === "headquarters"
                ? "Procure uma sede..."
                : showType === "non-headquarters"
                  ? "Procure um ponto de referência..."
                  : "Procure um ponto de referência..."
            }
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {showType === "headquarters"
                ? "Nenhuma sede encontrada."
                : "Nenhum ponto de referência encontrado."}
            </CommandEmpty>
            {multiple && (
              <CommandGroup>
                <div className="flex gap-2 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleSelectAll}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleDeselectAll}
                  >
                    Desmarcar todos
                  </Button>
                </div>
              </CommandGroup>
            )}
            <CommandGroup>
              {filteredReferencePoints.map((point) => (
                <CommandItem
                  value={`${point.name} ${point.name || ""}`}
                  key={point.id}
                  onSelect={() => handleSelect(point.id)}
                  className="flex flex-col items-start gap-1 p-3"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      {point.is_headquarters ? (
                        <Building2 className="h-4 w-4 text-blue-600" />
                      ) : (
                        <MapPin className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="font-medium">{point.name}</span>
                      {point.is_headquarters && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Sede
                        </span>
                      )}
                    </div>
                    <Check
                      className={cn(
                        "ml-auto",
                        multiple
                          ? Array.isArray(value) && value.includes(point.id)
                            ? "opacity-100"
                            : "opacity-0"
                          : point.id === value
                            ? "opacity-100"
                            : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ReferencePointSelect;
